import { useState } from "react";
import { TopBar } from "../../components/TopBar";
import { PageHeader } from "../../components/PageHeader";
import { EmptyState } from "../../components/EmptyState";
import { useProjects } from "../../context/ProjectContext";
import { useToast } from "../../context/ToastContext";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../api/client";
import { timeAgo } from "../../lib/format";
import type { FlaggedScore } from "../../api/types";

const STATUSES = ["pending", "approved", "rejected"] as const;
type StatusFilter = (typeof STATUSES)[number];

function reasonLabel(reason: string): string {
  switch (reason) {
    case "stddev_outlier":
      return "Statistical outlier";
    case "impossible_delta":
      return "Impossible delta";
    default:
      return reason;
  }
}

export function Moderation() {
  const { active } = useProjects();
  const pid = active?.id ?? "";
  const toast = useToast();
  const [filter, setFilter] = useState<StatusFilter>("pending");
  const { data, loading, error, reload } = useAsync(() => api.listFlagged(pid, filter), [pid, filter]);
  const [busy, setBusy] = useState<number | null>(null);

  const rows = data ?? [];

  async function approve(f: FlaggedScore) {
    setBusy(f.id);
    try {
      await api.resolveFlag(pid, f.id, "approved");
      toast(`Approved ${f.player_id}'s score`);
      reload();
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setBusy(null);
    }
  }

  async function reject(f: FlaggedScore) {
    setBusy(f.id);
    try {
      // The backend removes the entry (owner-routed) AND marks the flag rejected in one call.
      await api.resolveFlag(pid, f.id, "rejected");
      toast(`Removed ${f.player_id} from ${f.leaderboard_id}`);
      reload();
    } catch (e) {
      toast((e as Error).message, "err");
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <TopBar crumbs={<b>Moderation</b>} />
      <div className="content">
        <PageHeader
          title="Moderation"
          intent={
            <>
              Suspicious scores flagged by anti-cheat anomaly detection. On boards set to{" "}
              <b>hold flagged scores for review</b>, a flagged submission is held out of the leaderboard until
              you act: <b>Approve</b> admits it; <b>Reject</b> keeps it out. (On boards that only flag, the
              score already counts — Approve just clears the flag, Reject removes the entry.) Enable detection
              and the hold in the board editor's Anti-cheat section.
            </>
          }
        />

        <div className="seg" style={{ marginBottom: 16 }}>
          {STATUSES.map((s) => (
            <button key={s} data-active={filter === s} onClick={() => setFilter(s)}>
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        {loading && <p className="muted">Loading…</p>}
        {error && <p className="err">{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <EmptyState icon="✓" title={filter === "pending" ? "Nothing awaiting review" : `No ${filter} scores`}>
            {filter === "pending"
              ? "Flagged submissions will appear here once anti-cheat is enabled on a board."
              : `No scores have been ${filter}.`}
          </EmptyState>
        )}

        {rows.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Board</th>
                <th>Player</th>
                <th>Score</th>
                <th>Reason</th>
                <th>Detail</th>
                <th>Flagged</th>
                {filter === "pending" && <th></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.id}>
                  <td className="mono">{f.leaderboard_id}</td>
                  <td className="mono">{f.player_id}</td>
                  <td className="mono">{f.score}</td>
                  <td>
                    <span className="tag tag-coral">{reasonLabel(f.reason)}</span>
                  </td>
                  <td className="muted" style={{ maxWidth: 360, fontSize: 12 }}>
                    {f.detail?.message ?? ""}
                  </td>
                  <td className="muted">{timeAgo(f.created_at)}</td>
                  {filter === "pending" && (
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button
                        className="btn btn-sm btn-ghost"
                        disabled={busy === f.id}
                        onClick={() => approve(f)}
                      >
                        Approve
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        style={{ marginLeft: 6 }}
                        disabled={busy === f.id}
                        onClick={() => reject(f)}
                      >
                        Reject
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
